import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Calendar,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Eye,
  Trash2,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { PageHeader } from '@components/common/PageHeader';
import { getMyBookings } from '@mocks/bookings';

export const AppointmentHistory: React.FC = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(getMyBookings());
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time: string) => {
    return time;
  };

  const isUpcoming = (dateString: string) => {
    const bookingDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate >= today;
  };

  const handleCancelBooking = async (bookingId: number) => {
    if (!confirm('Are you sure you want to cancel this appointment?')) return;

    setCancellingId(bookingId);

    // Mock API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    console.log('✅ Mock: Booking cancelled', bookingId);

    // Update booking status to cancelled
    setBookings(
      bookings.map((b) =>
        b.id === bookingId
          ? { ...b, status: 'CANCELLED' as const, cancelled_at: new Date().toISOString() }
          : b
      )
    );

    toast.success('Appointment cancelled successfully');
    setCancellingId(null);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return {
          variant: 'success' as const,
          icon: <CheckCircle className="h-4 w-4" />,
          label: 'Confirmed',
        };
      case 'CANCELLED':
        return {
          variant: 'danger' as const,
          icon: <XCircle className="h-4 w-4" />,
          label: 'Cancelled',
        };
      case 'COMPLETED':
        return {
          variant: 'default' as const,
          icon: <CheckCircle className="h-4 w-4" />,
          label: 'Completed',
        };
      default:
        return {
          variant: 'warning' as const,
          icon: <AlertCircle className="h-4 w-4" />,
          label: status,
        };
    }
  };

  const upcomingBookings = bookings.filter(
    (b) => b.slot && isUpcoming(b.slot.date) && b.status === 'CONFIRMED'
  );

  const pastBookings = bookings.filter(
    (b) => b.slot && (!isUpcoming(b.slot.date) || b.status !== 'CONFIRMED')
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="My Appointments"
        description="View and manage your appointment bookings"
        actions={
          <Button onClick={() => navigate('/booking')} icon={<Plus className="h-4 w-4" />}>
            Book New Appointment
          </Button>
        }
      />

      {/* Upcoming Appointments */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-ncaa-primary" />
          Upcoming Appointments
        </h2>

        {upcomingBookings.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Upcoming Appointments</h3>
              <p className="text-gray-600 mb-6">You don't have any scheduled appointments.</p>
              <Button onClick={() => navigate('/booking')} icon={<Plus className="h-4 w-4" />}>
                Book an Appointment
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {upcomingBookings.map((booking) => {
              const statusConfig = getStatusConfig(booking.status);

              return (
                <Card key={booking.id}>
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{booking.center?.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{booking.center?.address}</p>
                      </div>
                      <Badge variant={statusConfig.variant} icon={statusConfig.icon}>
                        {statusConfig.label}
                      </Badge>
                    </div>

                    {/* Date and Time */}
                    <div className="space-y-2 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Calendar className="h-4 w-4 text-ncaa-primary" />
                        <span className="text-sm font-medium">
                          {booking.slot && formatDate(booking.slot.date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Clock className="h-4 w-4 text-ncaa-primary" />
                        <span className="text-sm">
                          {booking.slot && `${formatTime(booking.slot.start_time)} - ${formatTime(booking.slot.end_time)}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <MapPin className="h-4 w-4 text-ncaa-primary" />
                        <span className="text-sm">{booking.center?.city}, {booking.center?.state}</span>
                      </div>
                    </div>

                    {/* Booking Reference */}
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-600">
                        <strong>Reference:</strong> {booking.booking_reference}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancelBooking(booking.id)}
                        loading={cancellingId === booking.id}
                        icon={<Trash2 className="h-3 w-3" />}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Past Appointments */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-gray-600" />
          Past Appointments
        </h2>

        {pastBookings.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-gray-500">No past appointments</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {pastBookings.map((booking) => {
              const statusConfig = getStatusConfig(booking.status);

              return (
                <Card key={booking.id}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Center Info */}
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Center</p>
                        <p className="text-sm font-medium text-gray-900">{booking.center?.name}</p>
                        <p className="text-xs text-gray-500">{booking.center?.city}</p>
                      </div>

                      {/* Date */}
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {booking.slot && new Date(booking.slot.date).toLocaleDateString()}
                        </p>
                      </div>

                      {/* Time */}
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Time</p>
                        <p className="text-sm font-medium text-gray-900">
                          {booking.slot && `${formatTime(booking.slot.start_time)} - ${formatTime(booking.slot.end_time)}`}
                        </p>
                      </div>

                      {/* Reference */}
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Reference</p>
                        <p className="text-xs font-mono text-gray-900">{booking.booking_reference}</p>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex-shrink-0 ml-4">
                      <Badge variant={statusConfig.variant} icon={statusConfig.icon}>
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
